import { useEffect, useRef } from "react";

const GRID = 64;
const CYAN = "0, 240, 255";
const PURPLE = "93, 61, 211";
const DIRS = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
];

/** Futuristic circuit-board energy: light pulses travel along the grid, turning at intersections. Ambient — no mouse needed. */
export default function CircuitField({ className = "" }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let raf = null;
        let running = true;
        let w = 0;
        let h = 0;
        let pulses = [];
        let lastSpawn = 0;

        const maxPulses = () => (w < 768 ? 6 : 10);
        const snap = (v) => Math.round(v / GRID) * GRID;

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = canvas.clientWidth;
            h = canvas.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        const spawn = () => {
            const cols = Math.floor(w / GRID);
            const rows = Math.floor(h / GRID);
            if (cols < 2 || rows < 2) return;
            const horizontal = Math.random() < 0.5;
            const dir = horizontal ? (Math.random() < 0.5 ? 0 : 2) : Math.random() < 0.5 ? 1 : 3;
            const gx = Math.floor(Math.random() * cols) * GRID;
            const gy = Math.floor(Math.random() * rows) * GRID;
            pulses.push({
                x: gx,
                y: gy,
                dir,
                speed: 1.6 + Math.random() * 1.6,
                color: Math.random() < 0.7 ? CYAN : PURPLE,
                trail: [{ x: gx, y: gy }],
                maxTrail: 62,
                life: 0,
                maxLife: 500 + Math.random() * 400,
            });
        };

        const tick = (ts) => {
            if (!running) return;
            ctx.clearRect(0, 0, w, h);
            if (ts - lastSpawn > 450 && pulses.length < maxPulses()) {
                spawn();
                lastSpawn = ts;
            }

            pulses = pulses.filter(
                (p) => p.life < p.maxLife && p.x > -GRID && p.x < w + GRID && p.y > -GRID && p.y < h + GRID
            );

            for (const p of pulses) {
                const [dx, dy] = DIRS[p.dir];
                p.x += dx * p.speed;
                p.y += dy * p.speed;
                p.life += 1;
                p.trail.push({ x: p.x, y: p.y });
                if (p.trail.length > p.maxTrail) p.trail.shift();

                // right-angle turns at grid intersections
                const onGridX = Math.abs(p.x / GRID - Math.round(p.x / GRID)) < 0.06;
                const onGridY = Math.abs(p.y / GRID - Math.round(p.y / GRID)) < 0.06;
                if (onGridX && onGridY && Math.random() < 0.05) {
                    const options = dx !== 0 ? [1, 3] : [0, 2];
                    p.dir = options[Math.floor(Math.random() * 2)];
                    p.x = snap(p.x);
                    p.y = snap(p.y);
                }

                const fade = Math.min(1, p.life / 20) * Math.min(1, (p.maxLife - p.life) / 40);

                for (let i = 1; i < p.trail.length; i += 1) {
                    const a = (i / p.trail.length) * 0.65 * fade;
                    ctx.strokeStyle = `rgba(${p.color}, ${a})`;
                    ctx.lineWidth = 1.8;
                    ctx.lineCap = "round";
                    ctx.beginPath();
                    ctx.moveTo(p.trail[i - 1].x, p.trail[i - 1].y);
                    ctx.lineTo(p.trail[i].x, p.trail[i].y);
                    ctx.stroke();
                }

                ctx.beginPath();
                ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.color}, ${0.95 * fade})`;
                ctx.shadowColor = `rgba(${p.color}, 1)`;
                ctx.shadowBlur = 14;
                ctx.fill();
                ctx.shadowBlur = 0;
            }
            raf = requestAnimationFrame(tick);
        };

        const onVis = () => {
            running = !document.hidden;
            if (running) raf = requestAnimationFrame(tick);
            else if (raf) cancelAnimationFrame(raf);
        };

        resize();
        window.addEventListener("resize", resize);
        document.addEventListener("visibilitychange", onVis);
        raf = requestAnimationFrame(tick);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
